/* eslint-disable @typescript-eslint/no-explicit-any */
import { type AnyFormState } from '@tanstack/react-form'
import { format } from 'date-fns'
import { isFinite } from 'lodash'

/**
 * Extracts the user ID from an Auth0 user subject string.
 *
 * Auth0 user subjects typically follow the format "provider|userId" (e.g., "auth0|123456").
 * This function extracts the userId portion after the pipe separator.
 *
 * @param userSub - The Auth0 user subject string in the format "provider|userId"
 * @returns The user ID portion of the subject string
 *
 * @example
 * ```ts
 * const userId = convertUserSubToId("auth0|123456789");
 * // Returns: "123456789"
 * ```
 */
export const convertUserSubToId = (userSub: string): string => {
  return userSub.split('|')[1]
}

export const returnIfFormValuesHaveChanged = (form: AnyFormState, defaultValues: any) => {
  if (form.isPristine) {
    return false
  }
  return JSON.stringify(form.values) !== JSON.stringify(defaultValues)
}

export const returnTextForSaveChangesButton = (
  isSubmitted: boolean,
  isSubmitting: boolean,
  mutationError: any
) => {
  if (mutationError) return 'Retry'
  if (isSubmitting) return 'Saving...'
  if (isSubmitted) return 'Saved'
  return 'Save'
}

const TIME_STRING_FORMAT = {
  hour: '2-digit',
  minute: '2-digit',
} as const

/**
 * Converts a date string to a formatted time string in 12-hour format.
 *
 * Takes an ISO date string and returns a localized time string in US format
 * showing hours and minutes with AM/PM designation.
 *
 * @param date - An ISO 8601 date string (e.g., "2024-01-01T15:30:00Z")
 * @returns A formatted time string in "HH:MM AM/PM" format
 *
 * @example
 * ```ts
 * const timeString = convertDateToTimeString("2024-01-01T15:30:00Z");
 * // Returns: "3:30 PM" (depending on user's timezone)
 *
 * const morningTime = convertDateToTimeString("2024-01-01T09:15:00Z");
 * // Returns: "9:15 AM" (depending on user's timezone)
 * ```
 */
export const convertDateToTimeString = (date: string): string => {
  const dateObj = new Date(date)
  return dateObj.toLocaleTimeString('en-US', TIME_STRING_FORMAT)
}

export const convertDateToReadableString = (date: string, includeTime = false): string => {
  const dateObj = new Date(date)
  let options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }
  if (includeTime) {
    options = { ...options, ...TIME_STRING_FORMAT }
  }
  return dateObj.toLocaleDateString('en-US', options)
}

export const numberRangeValidator = ({
  value,
  min,
  max,
}: {
  value: any
  min?: number
  max?: number
}): string | null => {
  let rangeText = ''
  if (min !== undefined && max !== undefined) {
    rangeText = ` between ${min} and ${max}`
  } else if (min !== undefined) {
    rangeText = ` greater than ${min}`
  } else if (max !== undefined) {
    rangeText = ` less than ${max}`
  }
  const errorText = `This field must be a number${rangeText}`

  if (!isFinite(value)) {
    return errorText
  }
  if (min !== undefined && max !== undefined) {
    return value >= min && value <= max ? null : errorText
  }
  if (min !== undefined) {
    return value >= min ? null : errorText
  }
  if (max !== undefined) {
    return value <= max ? null : errorText
  }
  return null
}

export const dateWithoutTimezone = (date: Date) => {
  const tzoffset = date.getTimezoneOffset() * 60000 //offset in milliseconds
  const withoutTimezone = new Date(date.valueOf() - tzoffset).toISOString().slice(0, -1)
  return withoutTimezone
}

export const formatBillingDate = (dateString: string, includeYear = false) => {
  const date = new Date(dateString)
  return format(date, includeYear ? 'MMM d, yyyy' : 'MMM d')
}

export const CODEX_NOTIFICATION_TITLES = {
  info: 'A credit has been added to your account.',
  error: 'Enter payment details to continue using Codex.',
} as const

export const CODEX_NOTIFICATION_DESCRIPTIONS = {
  info: "You have used all of your free resolutions, so here's a credit of $10.00 to your account to continue using Codex on us. If you want to continue using Codex when your free credit expires, add a payment source to your account.",
  error:
    'You have exceeded the 1000 free usages monthly limit. In order to continue using Codex, please enter your payment details to pay-as-you-go for additional usages.',
} as const

export const DEFAULT_FALLBACK_ERROR_MESSAGE = 'An unknown error occurred'

/**
 * Extracts a human-readable error message from various error object formats.
 *
 * This function attempts to extract error messages from different error object structures
 * commonly used in API responses and JavaScript error handling. It follows a priority order:
 * 1. `error.response.data.detail` - Common in backend API responses
 * 2. `error.response.data.message` - Standard API error message field
 * 3. `error.response.data.error` - Alternative error field
 * 4. `error.response.data.errorMessage` - Another common error field
 * 5. `error.message` - Standard JavaScript Error message property
 * 6. `String(error)` - String representation of the error object
 * 7. 'An unknown error occurred' - Fallback for null/undefined errors
 *
 * @param error - The error object to extract a message from. Can be any type including
 *                null, undefined, Error objects, or API response error objects.
 * @returns A string containing the extracted error message or a fallback message.
 *
 * @example
 * ```typescript
 * // API error with detail field
 * const apiError = { response: { data: { detail: 'User not found' } } };
 * getErrorMessage(apiError); // Returns: 'User not found'
 *
 * // Standard JavaScript Error
 * const jsError = new Error('Something went wrong');
 * getErrorMessage(jsError); // Returns: 'Something went wrong'
 *
 * // Null error
 * getErrorMessage(null); // Returns: 'An unknown error occurred'
 * ```
 */
export const getErrorMessage = (error: any, fallbackMessage = DEFAULT_FALLBACK_ERROR_MESSAGE) => {
  switch (true) {
    // we seem to use `detail` across our backends
    case error?.response?.data?.detail && typeof error.response.data.detail === 'string':
      return error.response.data.detail
    // just in case. these are other industry standard keys
    case error?.response?.data?.message && typeof error.response.data.message === 'string':
      return error.response.data.message
    case error?.response?.data?.error && typeof error.response.data.error === 'string':
      return error.response.data.error
    case error?.response?.data?.errorMessage &&
      typeof error.response.data.errorMessage === 'string':
      return error.response.data.errorMessage
    case error?.message && typeof error.message === 'string':
      return error.message
    case error && typeof error === 'string':
      return error
    default:
      return fallbackMessage
  }
}

export const convertToEpochSeconds = (date: Date) => {
  return Math.floor(date.getTime() / 1000)
}

/**
 * Formats a percentage from numerator and denominator values with smart precision.
 *
 * Calculates a percentage from two numbers and formats it as a string with appropriate
 * decimal places. Returns whole numbers for 0% and 100%, and two decimal places for
 * all other values. Handles division by zero gracefully.
 *
 * @param numerator - The numerator value (top number in the fraction)
 * @param denominator - The denominator value (bottom number in the fraction)
 * @returns A formatted percentage string with "%" suffix
 *
 * @example
 * ```ts
 * formatPercentage(1, 4);    // Returns: "25.00%"
 * formatPercentage(0, 10);   // Returns: "0%"
 * formatPercentage(10, 10);  // Returns: "100%"
 * formatPercentage(1, 3);    // Returns: "33.33%"
 * formatPercentage(5, 0);    // Returns: "0%" (handles division by zero)
 * ```
 *
 * @note Special formatting rules:
 * - Returns "0%" when denominator is zero (to prevent division by zero)
 * - Shows whole numbers for exactly 0% and 100%
 * - Shows two decimal places for all other percentages
 */
export const formatPercentage = (numerator: number, denominator: number): string => {
  if (!denominator) return '0%'
  const percentage = (numerator / denominator) * 100
  return percentage === 0 || percentage === 100 ? `${percentage}%` : `${percentage.toFixed(2)}%`
}

export const convertSnakeCaseToTitleCase = (str: string, hasSpaces = true): string => {
  return str.replace(/_/g, hasSpaces ? ' ' : '').replace(/\b\w/g, (char) => char.toUpperCase())
}
